import * as React from 'react';
import styled from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { ListView } from '../listview';
import { IBaseComponentProps } from '../types';
import { ITimePickerProps, Meridiem } from './time-picker-props';
import { Callout } from '../callout';
import { timeValidator } from './time-validator';

interface IPickerProps extends Pick<ITimePickerProps, 'is12Hour' | 'hourStep' | 'minuteStep' | 'secondStep' | 'showSecond'>, IBaseComponentProps<HTMLDivElement> {
    meridiem: Meridiem;
    hour: string;
    itemHeight?: number;
    hidden?: boolean;
    minute: string;
    second: string;
    targetRef: React.RefObject<HTMLElement>;
    onDismiss?(): void;
    onChange(hour: string, minute: string, second: string, meridiem: Meridiem): void;
}

const getMarkupForItem = memoizeFunction((itemName: string): JSX.Element => <div>{itemName}</div>);

const getValueForMinutePicker = memoizeFunction((minuteStep = 1): string[] => {
    const minuteStart = 0;
    const minuteEnd = 60;
    const minutes: string[] = [];
    const minStep = minuteStep <= 0 ? 1 : minuteStep;
    let i = minuteStart;
    while (i < minuteEnd) {
        minutes.push((i < 10) ? (`0${i}`) : String(i));
        i += minStep;
    }
    return minutes;
});

const getValueForSecondPicker = memoizeFunction((secondStep = 1): string[] => getValueForMinutePicker(secondStep));

const getValueForHourPicker = memoizeFunction((is12Hour: boolean | undefined, hourStep = 1): string[] => {
    const startHour: number = is12Hour ? 1 : 0;
    const endHour: number = is12Hour ? 13 : 24;
    const hours: string[] = [];
    const hrStep = hourStep <= 0 ? 1 : hourStep;
    let i = startHour;
    while (i < endHour) {
        hours.push((i < 10) ? (`0${i}`) : String(i));
        i += hrStep;
    }
    return hours;
});

const MERIDIEM_PICKER_OPTIONS = ['AM', 'PM'];

const PickerList = styled(ListView)`
    float: left;
    height: 100%;
    overflow: hidden;
    width: 59px;
    &:hover {
        overflow-y: visible;
    }
    &:focus {
        outline: 0;
    }
    & .ap-listView-itemContentWrapper {
        border-style: none;
        border-width: ${({ theme: { timePicker: { composites } } }) => composites.optionsBorderWidth};
        padding: ${({ theme: { semantics: { gap } } }) => gap.l};
    }
`;

class PickerBase extends React.Component<IPickerProps> {
    render() {
        const { targetRef, className, hidden, onDismiss } = this.props;
        return (
            <Callout className={className}
                targetRef={targetRef}
                animateOnOpen={true}
                hidden={hidden}
                closeOnEscape={true}
                onDismiss={onDismiss}
                preventClickEventPropagation>
                {this.renderPickers()}
            </Callout>
        );
    }

    private renderPickers() {
        const { secondStep, second, is12Hour, hourStep, minuteStep, showSecond, hour, minute, meridiem } = this.props;
        return (
            <>
                {this.renderList(getValueForHourPicker(is12Hour, hourStep), this.onHourChange, hour, 'ap-hourPicker-wrapper')}
                {this.renderList(getValueForMinutePicker(minuteStep), this.onMinuteChange, minute, 'ap-minutePicker-wrapper')}
                {showSecond ? this.renderList(getValueForSecondPicker(secondStep), this.onSecondChange, second, 'ap-secondPicker-wrapper') : null}
                {is12Hour ? this.renderList(MERIDIEM_PICKER_OPTIONS, this.onMeridiemChange, meridiem, 'ap-meridiemPicker-wrapper') : null}
            </>
        );
    }

    private renderList(items: string[], onChange: (args: string[]) => void, selectedKey: string, className: string) {
        const { itemHeight } = this.props;
        return (
            <PickerList className={className}
                selectable="single"
                itemHeight={itemHeight}
                selectedKeys={[selectedKey]}
                allowKeyboardNavigation={true}
                onChange={onChange}
                items={items}
                isVirtualized={false}
                onRenderItemContent={getMarkupForItem}
            />
        );
    }

    private onHourChange = (selectedHour: string[]) => {
        const { is12Hour = false, minute, second, meridiem, hour: oldHour, onChange } = this.props;
        const hour: string = selectedHour.length === 0 ? oldHour : selectedHour[0];
        const isValid = timeValidator.isValidHour(hour, is12Hour);
        if (!isValid) {
            return;
        }
        const newMeridiem: Meridiem = is12Hour ? meridiem : Number(hour) > 11 ? Meridiem.PM : Meridiem.AM;
        onChange(hour, minute, second, newMeridiem);
    }

    private onMinuteChange = (selectedMinute: string[]) => {
        const { minute: oldMinute, second, meridiem, hour, onChange } = this.props;
        const minute: string = selectedMinute.length === 0 ? oldMinute : selectedMinute[0];
        const isValid = timeValidator.isValidMinute(minute);
        if (!isValid) {
            return;
        }
        onChange(hour, minute, second, meridiem);
    }

    private onSecondChange = (selectedSecond: string[]) => {
        const { minute, second: oldSecond, meridiem, hour, onChange } = this.props;
        const second: string = selectedSecond.length === 0 ? oldSecond : selectedSecond[0];
        const isValid = timeValidator.isValidSecond(second);
        if (!isValid) {
            return;
        }
        onChange(hour, minute, second, meridiem);
    }

    private onMeridiemChange = (selectedMeridiem: string[]) => {
        const { hour, minute, second, meridiem: oldMeridiem, onChange } = this.props;
        const meridiem = (selectedMeridiem.length === 0 ? oldMeridiem : selectedMeridiem[0]) as Meridiem;
        const isValid = timeValidator.isValidMeridiem(meridiem);
        if (!isValid) {
            return;
        }
        onChange(hour, minute, second, meridiem);
    }
}

export const Picker = styled(PickerBase)`
    overflow: hidden;
    font-size: 13px;
    height: 200px;
    & .ap-callout-content {
        border-radius: ${({ theme: { timePicker: { composites } } }) => composites.calloutBorderRadius};
        overflow: hidden;
    }
`;
